import React, { useState } from 'react';
import { Package, Trash2, Mail, Loader2, Truck, Plus } from 'lucide-react';
import RepairStockSearch from './RepairStockSearch';
import RepairLaborSuggestionModal from './RepairLaborSuggestionModal';
import { RepSection } from './reparationsUi';
import { fmtEuro } from './reparationsUtils';

const emptyPending = () => ({
  designation: '', reference: '', part_type: 'standard',
  qty: 1, prix_vente_prevu: '', prix_achat_unitaire: '', main_oeuvre: '', fournisseur: '',
});

const linePiecePrice = (line) => {
  if (line?.piece_price != null) return Number(line.piece_price) || 0;
  const unit = Number(line?.unit_price || 0);
  const catalogMo = Number(line?.main_oeuvre_catalog ?? line?.main_oeuvre ?? 0);
  return Math.max(0, unit - catalogMo);
};

const lineEffectiveUnit = (line) => linePiecePrice(line) + Number(line?.main_oeuvre || 0);

const lineLineTotal = (line) => lineEffectiveUnit(line) * (line?.qty || 1);

const RepairStockPartsPanel = ({
  api,
  deviceRef,
  magasinId,
  lines = [],
  pricingMode,
  quotedTotal,
  priceRepair,
  priceDelta,
  remise = 0,
  clientEmail,
  onUpdated,
}) => {
  const [busy, setBusy] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [error, setError] = useState('');
  const [moModal, setMoModal] = useState({ open: false, suggestion: null, partLabel: '' });
  const [applyingMo, setApplyingMo] = useState(false);
  const [moDrafts, setMoDrafts] = useState({});
  const [moSavingId, setMoSavingId] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingForm, setPendingForm] = useState(emptyPending());
  const [pendingBusy, setPendingBusy] = useState(false);

  const refreshLines = async () => {
    const res = await api.get(`/devices/${deviceRef}/repair-lines`);
    onUpdated?.(res.data);
    return res.data;
  };

  const openMoModal = (data, part) => {
    const suggestion = data?.mo_suggestion;
    if (!suggestion?.show && !(data?.lines?.length > 0)) return;
    setMoModal({
      open: true,
      suggestion: suggestion?.show
        ? { ...suggestion, current_remise: data?.remise ?? remise }
        : {
            show: true,
            mode: 'single_part',
            message: `Pièce ajoutée — total réparation ${fmtEuro(data?.price_repair)} €. Proposez une remise si nécessaire.`,
            current_remise: data?.remise ?? remise,
            price_repair: data?.price_repair,
            quoted_total: data?.quoted_total,
            price_delta: data?.price_delta,
            suggested_remise: 0,
            proposed_remise: data?.remise ?? remise,
            manual_only: true,
          },
      partLabel: part?.designation || part?.reference || '',
    });
  };

  const attach = async (part) => {
    if (!part?.reference || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/devices/${deviceRef}/repair-lines`, {
        reference: part.reference,
        qty: 1,
      });
      onUpdated?.(res.data);
      openMoModal(res.data, part);
    } catch (err) {
      const status = err?.response?.status;
      const canRecover = !status || status >= 500;
      if (canRecover) {
        try {
          const data = await refreshLines();
          const added = (data?.lines || []).some((l) => l.stock_reference === part.reference);
          if (added) {
            openMoModal(data, part);
            return;
          }
        } catch {
          /* ignore */
        }
      }
      setError(err?.response?.data?.detail || 'Impossible de joindre la pièce');
    }
    setBusy(false);
  };

  const applyMoAdjustment = async ({ remise: newRemise, note }) => {
    setApplyingMo(true);
    try {
      const res = await api.post(`/devices/${deviceRef}/repair-pricing-adjustment`, {
        remise: newRemise,
        note: note || undefined,
      });
      onUpdated?.(res.data);
      setMoModal({ open: false, suggestion: null, partLabel: '' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossible d\'appliquer la remise');
    }
    setApplyingMo(false);
  };

  const getMoDraft = (line) => {
    if (moDrafts[line.id] !== undefined) return moDrafts[line.id];
    return line.main_oeuvre != null ? String(line.main_oeuvre) : '';
  };

  const saveLineMo = async (line) => {
    const raw = getMoDraft(line);
    const parsed = parseFloat(String(raw).replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('MO invalide');
      return;
    }
    const current = Number(line.main_oeuvre || 0);
    if (Math.abs(parsed - current) < 0.005) {
      setMoDrafts((prev) => {
        const next = { ...prev };
        delete next[line.id];
        return next;
      });
      return;
    }
    setMoSavingId(line.id);
    setError('');
    try {
      const res = await api.patch(`/devices/${deviceRef}/repair-lines/${line.id}`, {
        main_oeuvre: parsed,
      });
      onUpdated?.(res.data);
      setMoDrafts((prev) => {
        const next = { ...prev };
        delete next[line.id];
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossible de mettre à jour la MO');
    }
    setMoSavingId(null);
  };

  const addPendingPart = async () => {
    const designation = (pendingForm.designation || '').trim();
    if (!designation) { setError('Désignation requise pour la pièce à commander'); return; }
    setPendingBusy(true);
    setError('');
    try {
      const res = await api.post(`/devices/${deviceRef}/repair-lines/pending`, {
        designation,
        reference: (pendingForm.reference || '').trim(),
        part_type: pendingForm.part_type,
        suivi_imei: pendingForm.part_type === 'serialise',
        qty: Math.max(1, parseInt(pendingForm.qty, 10) || 1),
        prix_vente_prevu: parseFloat(pendingForm.prix_vente_prevu) || 0,
        prix_achat_unitaire: parseFloat(pendingForm.prix_achat_unitaire) || 0,
        main_oeuvre: parseFloat(pendingForm.main_oeuvre) || 0,
        fournisseur: (pendingForm.fournisseur || '').trim(),
      });
      onUpdated?.(res.data);
      setPendingForm(emptyPending());
      setPendingOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossible d\'ajouter la pièce à commander');
    }
    setPendingBusy(false);
  };

  const detach = async (line) => {
    const isPending = line.status === 'pending_order';
    const msg = isPending
      ? 'Retirer cette pièce en commande de la réparation ?'
      : 'Retirer cette pièce et réintégrer le stock ?';
    if (!window.confirm(msg)) return;
    const lineId = line.id;
    setBusy(true);
    setError('');
    try {
      const res = await api.delete(`/devices/${deviceRef}/repair-lines/${lineId}`);
      onUpdated?.(res.data);
    } catch (err) {
      const status = err?.response?.status;
      const canRecover = !status || status >= 500;
      if (canRecover) {
        try {
          const data = await refreshLines();
          const stillThere = (data?.lines || []).some((l) => l.id === lineId);
          if (!stillThere) return;
        } catch {
          /* ignore */
        }
      }
      setError(err?.response?.data?.detail || 'Impossible de retirer la pièce');
    }
    setBusy(false);
  };

  const notifyClient = async () => {
    if (!clientEmail) return;
    setNotifyBusy(true);
    setError('');
    try {
      await api.post(`/devices/${deviceRef}/notify-price-update`, {});
      alert('Email envoyé au client.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Envoi email échoué');
    }
    setNotifyBusy(false);
  };

  const delta = priceDelta != null ? Number(priceDelta) : null;
  const showDelta = pricingMode === 'grounded' && delta != null && delta !== 0;

  return (
    <>
      <RepSection title="Pièces stock consommées" testId="repair-stock-parts-card">
        {showDelta && (
          <div className="mb-3 p-3 rounded-lg border border-amber-500/25 bg-amber-950/20 text-sm">
            <div className="text-[10px] font-bold text-amber-400 uppercase mb-1">Devis vs réel</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-200">
              <span>Devis initial : <strong>{fmtEuro(quotedTotal)} €</strong></span>
              <span>Total actuel : <strong>{fmtEuro(priceRepair)} €</strong></span>
              <span className={delta > 0 ? 'text-red-300' : 'text-emerald-300'}>
                {delta > 0 ? '+' : ''}{fmtEuro(delta)} €
              </span>
            </div>
          </div>
        )}

        {(remise || 0) > 0 && (
          <p className="text-xs text-emerald-400/90 mb-2">
            Remise appliquée : -{fmtEuro(remise)} €
          </p>
        )}

        {lines.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">Aucune pièce jointe — prix sur devis théorique.</p>
        ) : (
          <div className="space-y-2 mb-3">
            {lines.map((line) => {
              const piece = linePiecePrice(line);
              const catalogMo = Number(line.main_oeuvre_catalog ?? line.main_oeuvre ?? 0);
              const draftMo = getMoDraft(line);
              const previewMo = draftMo === '' ? catalogMo : (parseFloat(String(draftMo).replace(',', '.')) || 0);
              const previewTotal = (piece + previewMo) * (line.qty || 1);
              const moAdjusted = Math.abs(previewMo - catalogMo) >= 0.01;

              const isPending = line.status === 'pending_order';
              return (
              <div
                key={line.id}
                className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border text-sm ${isPending ? 'bg-amber-950/15 border-amber-500/25' : 'bg-black/25 border-white/10'}`}
                data-testid={`repair-line-${line.id}`}
              >
                <div className="min-w-0 flex items-start gap-2">
                  {isPending
                    ? <Truck className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    : <Package className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate flex items-center gap-1.5">
                      {line.stock_reference || line.designation}
                      {isPending && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-950/50 text-amber-300 border border-amber-500/30 uppercase tracking-wide">
                          En commande
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 truncate">{line.designation}</div>
                    <div className="text-xs text-gray-400 mt-0.5 flex flex-wrap items-center gap-x-1 gap-y-1">
                      <span>{line.qty || 1} × pièce {fmtEuro(piece)} €</span>
                      <span className="text-amber-400/90 inline-flex items-center gap-1">
                        + MO
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draftMo}
                          onChange={(e) => setMoDrafts((prev) => ({ ...prev, [line.id]: e.target.value }))}
                          onBlur={() => saveLineMo(line)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveLineMo(line);
                            }
                          }}
                          disabled={busy || moSavingId === line.id}
                          className="w-[4.5rem] rounded bg-black/40 border border-amber-500/35 text-amber-200 text-xs px-1.5 py-0.5 text-center tabular-nums focus:outline-none focus:border-amber-400 disabled:opacity-50"
                          title="Corriger la MO ou la mettre à 0 pour retirer la main d'œuvre"
                          data-testid={`repair-line-mo-${line.id}`}
                        />
                        €
                        {catalogMo > 0 && moAdjusted && (
                          <span className="text-[10px] text-gray-500 line-through ml-0.5" title="MO catalogue">
                            {fmtEuro(catalogMo)} €
                          </span>
                        )}
                        {moSavingId === line.id && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
                      </span>
                      <span className="text-white font-medium">
                        = {fmtEuro(previewTotal)} €
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => detach(line)}
                  disabled={busy}
                  className="shrink-0 p-2 rounded-lg bg-red-950/40 border border-red-500/25 text-red-300 hover:bg-red-950/60 disabled:opacity-40"
                  title={isPending ? 'Retirer la pièce en commande' : 'Retirer et réintégrer le stock'}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
            })}
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Joindre une pièce du stock {magasinId ? `(${magasinId})` : ''}
          </label>
          <RepairStockSearch
            api={api}
            magasinId={magasinId}
            onSelect={attach}
            disabled={busy}
            excludeRefs={lines.map((l) => l.stock_reference)}
          />
          {busy && (
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Mise à jour stock…
            </p>
          )}
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        <div className="mt-3 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={() => setPendingOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left text-[10px] font-bold text-amber-400/90 uppercase tracking-wider hover:text-amber-300"
            data-testid="pending-part-toggle"
          >
            <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Pièce à commander (pas en stock)</span>
            <Plus className={`w-4 h-4 transition-transform ${pendingOpen ? 'rotate-45' : ''}`} />
          </button>

          {pendingOpen && (
            <div className="mt-2 space-y-2 p-3 rounded-lg bg-amber-950/10 border border-amber-500/20" data-testid="pending-part-form">
              <p className="text-[11px] text-gray-400">
                La pièce est intégrée au prix client sans décompter le stock. Une commande fournisseur est créée automatiquement (visible dans « Commandes pièces »).
              </p>
              <input
                type="text"
                value={pendingForm.designation}
                onChange={(e) => setPendingForm((p) => ({ ...p, designation: e.target.value }))}
                placeholder="Désignation * (ex : Écran iPhone 12)"
                className="w-full rounded bg-black/40 border border-white/10 text-white text-sm px-2.5 py-2 min-h-[38px] focus:outline-none focus:border-amber-400/50"
                data-testid="pending-desig"
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={pendingForm.part_type}
                  onChange={(e) => setPendingForm((p) => ({ ...p, part_type: e.target.value }))}
                  className="rounded bg-black/40 border border-white/10 text-white text-sm px-2 py-2 min-h-[38px]"
                >
                  <option value="standard">Standard</option>
                  <option value="piece_detachee">Pièce détachée</option>
                  <option value="serialise">Sérialisé (n° série/IMEI)</option>
                </select>
                <input
                  type="number" min="1"
                  value={pendingForm.qty}
                  onChange={(e) => setPendingForm((p) => ({ ...p, qty: e.target.value }))}
                  placeholder="Qté"
                  className="rounded bg-black/40 border border-white/10 text-white text-sm px-2 py-2 min-h-[38px] text-center"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[9px] text-gray-500 uppercase">Prix vente</label>
                  <input type="number" step="0.01" value={pendingForm.prix_vente_prevu}
                    onChange={(e) => setPendingForm((p) => ({ ...p, prix_vente_prevu: e.target.value }))}
                    placeholder="0.00"
                    className="w-full rounded bg-black/40 border border-white/10 text-white text-sm px-2 py-2 min-h-[38px]"
                    data-testid="pending-vente" />
                </div>
                <div>
                  <label className="text-[9px] text-gray-500 uppercase">Prix achat</label>
                  <input type="number" step="0.01" value={pendingForm.prix_achat_unitaire}
                    onChange={(e) => setPendingForm((p) => ({ ...p, prix_achat_unitaire: e.target.value }))}
                    placeholder="0.00"
                    className="w-full rounded bg-black/40 border border-white/10 text-white text-sm px-2 py-2 min-h-[38px]" />
                </div>
                <div>
                  <label className="text-[9px] text-gray-500 uppercase">MO</label>
                  <input type="number" step="0.01" value={pendingForm.main_oeuvre}
                    onChange={(e) => setPendingForm((p) => ({ ...p, main_oeuvre: e.target.value }))}
                    placeholder="0.00"
                    className="w-full rounded bg-black/40 border border-white/10 text-white text-sm px-2 py-2 min-h-[38px]" />
                </div>
              </div>
              <input
                type="text"
                value={pendingForm.fournisseur}
                onChange={(e) => setPendingForm((p) => ({ ...p, fournisseur: e.target.value }))}
                placeholder="Fournisseur (optionnel)"
                className="w-full rounded bg-black/40 border border-white/10 text-white text-sm px-2.5 py-2 min-h-[38px]"
              />
              <button
                type="button"
                onClick={addPendingPart}
                disabled={pendingBusy}
                className="w-full min-h-[40px] px-3 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="pending-submit"
              >
                {pendingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                Ajouter la pièce en commande
              </button>
            </div>
          )}
        </div>

        {clientEmail && lines.length > 0 && (
          <button
            type="button"
            onClick={notifyClient}
            disabled={notifyBusy || busy}
            className="mt-3 w-full min-h-[40px] px-3 rounded-lg border border-white/15 hover:bg-white/5 text-sm font-medium text-gray-300 flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="notify-price-update-btn"
          >
            {notifyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Prévenir le client (optionnel)
          </button>
        )}
      </RepSection>

      <RepairLaborSuggestionModal
        open={moModal.open}
        suggestion={moModal.suggestion}
        partLabel={moModal.partLabel}
        applying={applyingMo}
        onClose={() => setMoModal({ open: false, suggestion: null, partLabel: '' })}
        onApply={applyMoAdjustment}
      />
    </>
  );
};

export default RepairStockPartsPanel;
